/**
 * A2: read-only payoff ESTIMATE — always labeled as an estimate; degrades to a
 * balance-only quote when the server cannot resolve a per-diem.
 */

import React, { useState } from 'react';
import { customerPortalApi, PayoffQuoteResponse } from '../../../services/customerPortalApi';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';

interface Props {
  accountUuid: string;
  language?: PortalLanguage;
}

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const PayoffQuoteCard: React.FC<Props> = ({ accountUuid, language = 'en' }) => {
  const copy = PORTAL_COPY[language];
  const today = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);
  const [quote, setQuote] = useState<PayoffQuoteResponse | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchQuote = async () => {
    setLoading(true);
    setError(false);
    try {
      setQuote(await customerPortalApi.getPayoffQuote(accountUuid, asOfDate));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-base font-semibold text-gray-900">{copy.payoffTitle}</h2>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <label className="flex-1 text-sm text-gray-700">
            {copy.payoffDateLabel}
            <input
              type="date"
              min={today}
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </label>
          <button
            onClick={fetchQuote}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {copy.payoffGetQuote}
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{copy.loadError}</p>}
        {quote && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{copy.payoffBalance}</span>
              <span className="font-medium text-gray-900">
                {money(quote.current_total_balance)}
              </span>
            </div>
            {quote.per_diem_available && quote.per_diem != null ? (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {copy.payoffPerDiem} × {quote.per_diem_days}
                </span>
                <span className="font-medium text-gray-900">{money(quote.per_diem)}</span>
              </div>
            ) : (
              <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                {copy.payoffUnavailable}
              </p>
            )}
            <div className="flex justify-between border-t border-gray-100 pt-2">
              <span className="text-sm font-semibold text-gray-900">{copy.payoffEstimated}</span>
              <span className="text-lg font-bold text-blue-900">
                {money(quote.estimated_payoff)}
              </span>
            </div>
            <p className="text-xs text-gray-500">{copy.payoffDisclaimer}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayoffQuoteCard;
